// @ts-check

/**
 * Catalog size for an admin-generated puzzle photo.
 * Workers AI text-to-image models cap each side at 2048, so the photo is
 * generated at the largest exact 16:9 that stays under that cap and then
 * resampled up to this size in the admin dashboard.
 */
export const GENERATED_PUZZLE_WIDTH = 3200;
export const GENERATED_PUZZLE_HEIGHT = 1800;

/** Largest exact 16:9 frame inside the Workers AI 256–2048 pixel limit. */
export const NATIVE_GEN_WIDTH = 1920;
export const NATIVE_GEN_HEIGHT = 1080;

/** Smaller 16:9 used when a model rejects the larger frame. */
export const NATIVE_GEN_FALLBACK_WIDTH = 1024;
export const NATIVE_GEN_FALLBACK_HEIGHT = 576;

/** JPEG quality for the file saved to the catalog. */
export const GENERATED_JPEG_QUALITY = 0.93;

/**
 * Unsharp amount applied after the upscale.
 * High enough to restore edges lost in resampling, low enough to avoid halos.
 */
export const GENERATED_SHARPEN_AMOUNT = 0.65;

/** Words that should not be baked into the picture as a subject. */
const GENERIC_CATEGORIES = new Set(['', 'uncategorized', 'other', 'none']);

/**
 * Elements a stock photograph should not contain.
 * Passed as `negative_prompt` to models that accept one, and echoed in the
 * positive prompt for models that only read a single string.
 */
export const STOCK_PHOTO_NEGATIVE_PROMPT = [
  'blurry', 'soft focus', 'out of focus', 'motion blur', 'low resolution', 'pixelated',
  'jpeg artifacts', 'watermark', 'text', 'logo', 'signature', 'border', 'frame',
  'illustration', 'painting', 'cartoon', 'anime', 'cgi', '3d render',
  'oversaturated', 'plastic', 'deformed', 'duplicate'
].join(', ');

/** Default style modifiers for AI-generated stock photos. */
export const DEFAULT_AI_PROMPT_STYLE_SUFFIX =
  'horizontal 16:9 photograph, natural true-to-life color, realistic daylight, tack-sharp focus, crisp edges, fine surface texture, high microcontrast, clear detail from foreground to background, clean editorial composition';

/**
 * Stock-photo prompt. The subject comes from the admin title, category, and tags.
 * The rest asks for a sharp photograph rather than a cinematic illustration.
 *
 * @param {{ title?: string, category?: string, tags?: string[], styleSuffix?: string }} [fields]
 * @returns {string}
 */
export function buildStockPhotoPrompt(fields = {}) {
  const title = String(fields.title || 'scenic landscape').trim() || 'scenic landscape';
  const category = String(fields.category || '').trim();
  const categoryLine = GENERIC_CATEGORIES.has(category.toLowerCase()) ? '' : category;
  const tags = Array.isArray(fields.tags)
    ? fields.tags.map((tag) => String(tag || '').trim()).filter(Boolean).slice(0, 8)
    : [];
  const styleSuffix = String(fields.styleSuffix || '').trim() || DEFAULT_AI_PROMPT_STYLE_SUFFIX;

  return [
    `Professional stock photograph of ${title}`,
    categoryLine ? `${categoryLine} subject` : '',
    tags.length ? tags.join(', ') : '',
    styleSuffix,
    'no text, no watermark, no logo, no border, no illustration, no painting, no cartoon, no CGI, no blur'
  ].filter(Boolean).join('. ');
}

/**
 * Generation attempts, best photoreal model first.
 * FLUX.2 klein 9B is tried at 1920×1080, then at 1024×576 if that size is rejected.
 * SDXL Lightning accepts an explicit size up to 2048 and a negative prompt.
 *
 * @type {Array<{
 *   model: string,
 *   width: number,
 *   height: number,
 *   transport: 'multipart' | 'json',
 *   num_steps?: number,
 *   guidance?: number,
 *   negativePrompt?: string
 * }>}
 */
export const PUZZLE_IMAGE_MODELS = [
  {
    model: '@cf/black-forest-labs/flux-2-klein-9b',
    width: NATIVE_GEN_WIDTH,
    height: NATIVE_GEN_HEIGHT,
    transport: 'multipart'
  },
  {
    model: '@cf/black-forest-labs/flux-2-klein-9b',
    width: NATIVE_GEN_FALLBACK_WIDTH,
    height: NATIVE_GEN_FALLBACK_HEIGHT,
    transport: 'multipart'
  },
  {
    model: '@cf/bytedance/stable-diffusion-xl-lightning',
    width: NATIVE_GEN_WIDTH,
    height: NATIVE_GEN_HEIGHT,
    transport: 'json',
    num_steps: 8,
    guidance: 1,
    negativePrompt: STOCK_PHOTO_NEGATIVE_PROMPT
  }
];

/**
 * Payload for `env.AI.run`. Multipart models receive prompt, width, and height
 * as form fields. JSON models also receive the negative prompt and guidance.
 *
 * @param {{ model: string, width: number, height: number, transport: 'multipart' | 'json', num_steps?: number, guidance?: number, negativePrompt?: string }} attempt
 * @param {string} prompt
 * @returns {Record<string, any>}
 */
export function buildWorkersAiImageInput(attempt, prompt) {
  if (attempt.transport === 'multipart') {
    const form = new FormData();
    form.append('prompt', prompt);
    form.append('width', String(attempt.width));
    form.append('height', String(attempt.height));
    const formResponse = new Response(form);
    return {
      multipart: {
        body: formResponse.body,
        contentType: formResponse.headers.get('content-type') || 'multipart/form-data'
      }
    };
  }
  return {
    prompt,
    width: attempt.width,
    height: attempt.height,
    num_steps: attempt.num_steps,
    guidance: attempt.guidance,
    negative_prompt: attempt.negativePrompt
  };
}

/**
 * Intermediate canvas sizes for a stepwise 2× enlarge, then the exact target.
 * Doubling before the final draw keeps each resample under 2×, which holds
 * edges better than one jump from a small frame to 3200.
 *
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @param {number} [targetWidth]
 * @param {number} [targetHeight]
 * @returns {Array<{ width: number, height: number }>}
 */
export function upscaleSteps(sourceWidth, sourceHeight, targetWidth = GENERATED_PUZZLE_WIDTH, targetHeight = GENERATED_PUZZLE_HEIGHT) {
  const steps = [];
  let w = Math.max(1, sourceWidth | 0);
  let h = Math.max(1, sourceHeight | 0);
  if (w === targetWidth && h === targetHeight) return steps;
  while (w * 2 <= targetWidth && h * 2 <= targetHeight) {
    w *= 2;
    h *= 2;
    steps.push({ width: w, height: h });
  }
  if (w !== targetWidth || h !== targetHeight) {
    steps.push({ width: targetWidth, height: targetHeight });
  }
  return steps;
}

/**
 * 3×3 unsharp mask. Flat areas stay put. Edges gain local contrast.
 *
 * @param {Uint8ClampedArray} src
 * @param {number} width
 * @param {number} height
 * @param {number} [amount]
 * @returns {Uint8ClampedArray}
 */
export function unsharpMask(src, width, height, amount = GENERATED_SHARPEN_AMOUNT) {
  const out = new Uint8ClampedArray(src.length);
  const kernel = [1, 2, 1, 2, 4, 2, 1, 2, 1];
  const kernelSum = 16;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const base = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) {
        let acc = 0;
        let ki = 0;
        for (let ky = -1; ky <= 1; ky++) {
          const yy = Math.min(height - 1, Math.max(0, y + ky));
          for (let kx = -1; kx <= 1; kx++) {
            const xx = Math.min(width - 1, Math.max(0, x + kx));
            acc += src[(yy * width + xx) * 4 + c] * kernel[ki];
            ki += 1;
          }
        }
        const blurred = acc / kernelSum;
        const sharp = src[base + c] + amount * (src[base + c] - blurred);
        out[base + c] = sharp < 0 ? 0 : sharp > 255 ? 255 : Math.round(sharp);
      }
      out[base + 3] = src[base + 3];
    }
  }
  return out;
}
